/**
 * useAuth — bound to the web app's shared AuthCore. The hook itself lives
 * in @wg/hooks; this module just glues the singleton core in. Same surface
 * as before: login/register/loginWithGoogle/createGuest/logout/deleteAccount.
 */
import { useCallback, useState } from 'react';
import { makeUseAuth } from '@wg/hooks/auth';
import { auth } from '../client';
import { signInWithGoogle as ssoGoogle } from '../auth';

const useSharedAuth = makeUseAuth(auth);

export function useAuth() {
  const base = useSharedAuth();
  const [googleError, setGoogleError] = useState<string | null>(null);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    try {
      const result = await ssoGoogle(idToken);
      // After SSO, AuthProvider's onAuthEvent listener picks up the new
      // session and sets authState. We just clear local error.
      setGoogleError(null);
      return result;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setGoogleError(e?.response?.data?.message || 'Google sign-in failed');
      throw err;
    }
  }, []);

  return {
    ...base,
    loginWithGoogle,
    authError: base.authError ?? googleError,
  };
}
